// Apolo IDE — S5 FROZEN CONTRACTS (Subagents · Keep Rate · Harness A/B).
//
// Sessão 5/5 do sprint Kiro+Cursor. Estes shapes são CONGELADOS após Phase B
// (tag `chore: arch baseline`): runtime de subagent, amostra de Keep Rate e as
// variantes/resultados do Harness A/B. JS puro com JSDoc (o projeto não tem TS);
// validadores leves em vez de `any`. Sem I/O, sem imports — domínio puro
// (Dependency Rule), igual ao injectionBudget do Worker.
//
// LEIS embutidas aqui (testáveis):
//  - Subagent: SÓ `summary` cruza a fronteira pro pai. Contexto pesado fica
//    isolado. O budget do pai ENCOLHE, não cresce (lição Apolo×Cursor).
//  - Evaluator (A LEI): A/B PROPÕE craft, NUNCA faz merge. Decisão é humana.
//  - honest-green continua o juiz final; oracle pass-rate é o sinal do harness.

// ───────────────────────── Subagent ─────────────────────────

/** @typedef {'explore'|'bash'|'browser'} SubagentKind */
/** @typedef {'foreground'|'background'} SubagentMode */

/**
 * @typedef {Object} SubagentRun
 * @property {string} id
 * @property {SubagentKind} kind
 * @property {SubagentMode} mode
 * @property {string} [parentTaskId]  // task (S2) que spawnou
 * @property {string} input
 * @property {string} summary         // SÓ isto volta ao pai
 * @property {string[]} [artifacts]   // refs (paths/ids), nunca conteúdo pesado
 */

/** Os 3 built-in (frozen). */
export const SUBAGENT_KINDS = Object.freeze(['explore', 'bash', 'browser']);
export const SUBAGENT_MODES = Object.freeze(['foreground', 'background']);

/**
 * LAW (budget): o summary que cruza pro pai é limitado. ~4 chars/token (mesmo
 * proxy do injectionBudget do Worker); o cap garante que, por pior que seja o
 * output bruto do subagent, o pai recebe um payload pequeno e provável-bounded.
 */
export const SUBAGENT_SUMMARY_MAX_TOKENS = 512;
/** Profundidade máxima de aninhamento (anti-loop infinito). */
export const SUBAGENT_MAX_DEPTH = 3;
/** Concorrência máxima de subagents em background. */
export const SUBAGENT_MAX_CONCURRENCY = 4;

/** @param {unknown} v @returns {v is SubagentKind} */
export function isSubagentKind(v) {
  return typeof v === 'string' && SUBAGENT_KINDS.includes(v);
}

/**
 * Runtime validation of a SubagentRun. A fronteira pro pai SÓ pode carregar
 * estes campos — `summary` string obrigatório; nada de `rawOutput`/`context`.
 * @param {unknown} v @returns {v is SubagentRun}
 */
export function isSubagentRun(v) {
  if (!v || typeof v !== 'object') return false;
  const r = /** @type {Record<string, unknown>} */ (v);
  if (typeof r.id !== 'string' || !r.id) return false;
  if (!isSubagentKind(r.kind)) return false;
  if (r.mode !== 'foreground' && r.mode !== 'background') return false;
  if (typeof r.input !== 'string') return false;
  if (typeof r.summary !== 'string') return false;
  if (r.parentTaskId != null && typeof r.parentTaskId !== 'string') return false;
  if (r.artifacts != null && !Array.isArray(r.artifacts)) return false;
  return true;
}

/** Campos que JAMAIS podem aparecer no retorno pro pai (leak guard). */
export const SUBAGENT_FORBIDDEN_RETURN_KEYS = Object.freeze([
  'rawOutput', 'raw', 'context', 'messages', 'transcript', 'stdout', 'stderr', 'dom', 'html',
]);

// ───────────────────────── Keep Rate ─────────────────────────

/**
 * @typedef {Object} KeepRateSurvival
 * @property {boolean} 1h
 * @property {boolean} 1d
 * @property {boolean} 7d
 */
/**
 * @typedef {Object} KeepRateSample
 * @property {string} diffId
 * @property {number} generatedAt          // epoch ms
 * @property {KeepRateSurvival} survived
 */

/** Janelas de sobrevivência (frozen). */
export const KEEP_RATE_WINDOWS = Object.freeze(['1h', '1d', '7d']);
export const KEEP_RATE_WINDOW_MS = Object.freeze({
  '1h': 3600000,
  '1d': 86400000,
  '7d': 604800000,
});

/** @param {unknown} v @returns {v is KeepRateSample} */
export function isKeepRateSample(v) {
  if (!v || typeof v !== 'object') return false;
  const s = /** @type {Record<string, unknown>} */ (v);
  if (typeof s.diffId !== 'string' || !s.diffId) return false;
  if (!Number.isFinite(s.generatedAt)) return false;
  const sv = s.survived;
  if (!sv || typeof sv !== 'object') return false;
  const w = /** @type {Record<string, unknown>} */ (sv);
  return KEEP_RATE_WINDOWS.every((k) => typeof w[k] === 'boolean');
}

// ───────────────────────── Harness A/B ─────────────────────────

/**
 * @typedef {Object} HarnessConfig
 * @property {'tiered'|'flat'} constitution
 * @property {'lean'|'full'} budget
 * @property {boolean} hooks
 */
/**
 * @typedef {Object} HarnessVariant
 * @property {string} id
 * @property {HarnessConfig} config
 */
/**
 * @typedef {Object} HarnessResult
 * @property {string} variantId
 * @property {number} oraclePassRate   // sinal-keystone (oracle é o gate verificável)
 * @property {number} latency          // ms média
 * @property {number} tokenEff         // tokens por task green (menor = melhor)
 * @property {number} cacheHit         // fração 0..1
 * @property {number} toolCalls        // média por run
 * @property {number} keepRate         // fração 0..1 (2ª via da eval keystone)
 */

export const HARNESS_CONSTITUTIONS = Object.freeze(['tiered', 'flat']);
export const HARNESS_BUDGETS = Object.freeze(['lean', 'full']);

/** @param {unknown} v @returns {v is HarnessVariant} */
export function isHarnessVariant(v) {
  if (!v || typeof v !== 'object') return false;
  const x = /** @type {Record<string, unknown>} */ (v);
  if (typeof x.id !== 'string' || !x.id) return false;
  const c = x.config;
  if (!c || typeof c !== 'object') return false;
  const cc = /** @type {Record<string, unknown>} */ (c);
  return (
    HARNESS_CONSTITUTIONS.includes(/** @type {string} */ (cc.constitution)) &&
    HARNESS_BUDGETS.includes(/** @type {string} */ (cc.budget)) &&
    typeof cc.hooks === 'boolean'
  );
}

/** @param {unknown} v @returns {v is HarnessResult} */
export function isHarnessResult(v) {
  if (!v || typeof v !== 'object') return false;
  const r = /** @type {Record<string, unknown>} */ (v);
  if (typeof r.variantId !== 'string' || !r.variantId) return false;
  return ['oraclePassRate', 'latency', 'tokenEff', 'cacheHit', 'toolCalls', 'keepRate']
    .every((k) => Number.isFinite(r[k]));
}
